import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Paper, FormGroup, FormControlLabel, Switch, Card, CardMedia, CardContent, Button, Chip, Snackbar, Alert, Menu, MenuItem, RadioGroup, Radio, FormControl, FormLabel, Dialog, DialogTitle, DialogContent, DialogActions, TextField } from '@mui/material';
import { ShoppingCart, Save } from '@mui/icons-material';
// @ts-ignore
import { Resizable } from 're-resizable';
import ElementStyleEditorModal from './ElementStyleEditorModal'; // Bu zaten vardı
import InteractiveProductCardPreview from './InteractiveProductCardPreview'; // YENİ: Yeni bileşeni import et
import FilterPanelPreview from './FilterPanelPreview';
import BreadcrumbPreview from './BreadcrumbPreview';
import { CardRendererOptionPreview } from './CardRendererTypePreview';
import { getProductSettings, saveProductSettings } from '../../../services/ecommerceApi';
import { useTranslation } from 'react-i18next';

// Ayar anahtarlarını daha güvenli hale getirmek için bir tip oluşturalım
type DisplaySettingKeys = 'hideOutOfStock' | 'hideProductsWithoutImage' | 'showBreadcrumb' | 'productCardStyle';

// Resim geçiş buton stilleri
type ImageNavigationStyle = 'numbered' | 'circle' | 'dot';
type FilterMode = 'off' | 'standard' | 'advanced';
type CardRendererType = 'legacy' | 'fixedCardV2' | 'b2bOptimized';

// Örnek ürün verisi
const mockProduct = {
  id: 'mock-1',
  name: 'Sample Product Name',
  imageUrl: '/assets/gtslogo.jpg', // Ana resim
  imageUrls: [ // Çoklu resimler için dizi
    '/assets/gtslogo.jpg',
    '/assets/anket_yonetimi.png',
    '/assets/bildirim_ekran.png',
    '/assets/genel_gorev_ekran.png'
  ],
  stockCode: 'SKU12345',
  salePrice: 199.99,
  saleCurrency: 'TL',
  quantity: 3,
};

// Ana ayar tipinden sadece bu bileşeni ilgilendiren kısımları al
interface DisplaySettings {
  hideOutOfStock?: boolean;
  hideProductsWithoutImage?: boolean;
  showBreadcrumb?: boolean;
  productCardStyle?: any; // Made optional
}

interface ProductDisplaySettingsProps {
  settings: DisplaySettings;
  onSettingsChange: (setting: any, value: any) => void;
  companyId: string | null; // Allow null
  configId: string | null; // SQL ConfigId
}

type FilterSectionKey = 'sort' | 'size' | 'gender' | 'brand' | 'color' | 'category' | 'price';

interface FilterSidebarAdvancedState {
  layoutPreset: 'standard' | 'advanced';
  showSortSection: boolean;
  showClearAllButton: boolean;
  sections: Record<FilterSectionKey, { enabled: boolean }>;
}

const defaultAdvancedFilterSettings: FilterSidebarAdvancedState = {
  layoutPreset: 'standard',
  showSortSection: true,
  showClearAllButton: true,
  sections: {
    sort: { enabled: true },
    size: { enabled: true },
    gender: { enabled: true },
    brand: { enabled: true },
    color: { enabled: true },
    category: { enabled: true },
    price: { enabled: true },
  },
};

const defaultFixedCardV2Settings = {
  cardWidth: 280,
  cardHeight: 440,
  imageHeight: 220,
  priceColor: '#1976d2',
  priceFrameBorderColor: '#f5c04f',
  priceFrameTextColor: '#1f2937',
};

const clampNumber = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));

const ProductDisplaySettings: React.FC<ProductDisplaySettingsProps> = ({ settings, onSettingsChange, companyId, configId }) => {
  const { t } = useTranslation('salesSystem');
  const [cardSize, setCardSize] = useState({ width: 280, height: 384 });
  const [imageHeight, setImageHeight] = useState(192);
  const [cardRendererType, setCardRendererType] = useState<CardRendererType>('legacy');
  const [fixedCardV2Settings, setFixedCardV2Settings] = useState(defaultFixedCardV2Settings);
  const [fixedCardV2Inputs, setFixedCardV2Inputs] = useState({
    cardWidth: String(defaultFixedCardV2Settings.cardWidth),
    cardHeight: String(defaultFixedCardV2Settings.cardHeight),
    imageHeight: String(defaultFixedCardV2Settings.imageHeight),
  });
  const [isSaving, setIsSaving] = useState(false);
  const [activeImageIndex, setActiveImageIndex] = useState(0); // Aktif resim indeksi için state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });
  
  // Kart bileşenlerinin gösterim ayarları
  const [cardSettings, setCardSettings] = useState({
    showProductName: true,
    showSku: true,
    showPrice: true,
    showStockStatus: true,
    showAddToCartButton: true,
    lowStockThreshold: 5,
  });

  // Mobil kart düzeni için state
  const [mobileCardLayout, setMobileCardLayout] = useState<'single' | 'double'>('double');

  // Element stilleri için state
  const [elementStyles, setElementStyles] = useState({
    productName: { fontSize: '0.875rem', fontWeight: 600, color: 'inherit', fontStyle: 'normal', fontFamily: 'Arial' },
    sku: { fontSize: '0.75rem', fontWeight: 400, color: 'inherit', fontStyle: 'normal', fontFamily: 'Arial' },
    price: { fontSize: '1.125rem', fontWeight: 'bold', color: '#1976d2', fontStyle: 'normal', fontFamily: 'Arial' },
    stockStatus: { fontSize: '0.75rem', fontWeight: 400, color: 'inherit', fontStyle: 'normal', fontFamily: 'Arial' },
    addToCartButton: { backgroundColor: '#0f172a', color: '#f8fafc', hoverBackgroundColor: '#1e293b' },
  });
  
  // Resim geçiş stili için state'i geri ekliyoruz.
  const [imageNavigationStyle, setImageNavigationStyle] = useState<ImageNavigationStyle>('numbered');
  const [displaySettings, setDisplaySettings] = useState({
    hideOutOfStock: settings?.hideOutOfStock || false,
    hideProductsWithoutImage: settings?.hideProductsWithoutImage || false,
    showBreadcrumb: settings?.showBreadcrumb !== false,
  });
  const [filterSidebarSettings, setFilterSidebarSettings] = useState({
    enabled: false,
    showResultsCount: true,
    alignLeft: false,
    showClearButton: true,
    showSubcategoryPanel: true,
    showFilterPanel: true,
    showColumnSwitcher: false,
    defaultDesktopColumns: 5,
    showCategoryFilter: true,
    showBrandFilter: true,
    showPriceFilter: true,
    showVariantFilters: true,
    showOptionCounts: true,
    showInStockToggle: true,
  });
  const [filterMode, setFilterMode] = useState<FilterMode>('off');
  const [filterSidebarAdvanced, setFilterSidebarAdvanced] = useState<FilterSidebarAdvancedState>(defaultAdvancedFilterSettings);
  
  // Sağ tık menüsü için state
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    target: keyof typeof elementStyles;
  } | null>(null);

  // Stil düzenleme modalı için state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingElementKey, setEditingElementKey] = useState<keyof typeof elementStyles | null>(null);
  
  // Oto-kayıt için debounce timer
  const autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [productSettingsId, setProductSettingsId] = useState<string | null>(null);
  const isInitialLoadRef = useRef(true); // İlk yükleme kontrolü için

  useEffect(() => {
    setDisplaySettings({
      hideOutOfStock: settings?.hideOutOfStock || false,
      hideProductsWithoutImage: settings?.hideProductsWithoutImage || false,
      showBreadcrumb: settings?.showBreadcrumb !== false,
    });
  }, [settings?.hideOutOfStock, settings?.hideProductsWithoutImage, settings?.showBreadcrumb]);


  // SQL'den ayarları yükle
  useEffect(() => {
    const loadProductSettings = async () => {
      if (!configId || !companyId) { // ✅ companyId null check
        console.log('⏭️ [PRODUCT DISPLAY] ConfigId veya CompanyId yok, yükleme atlanıyor');
        return;
      }
      
      try {
        console.log('🔄 [PRODUCT DISPLAY] SQL den ayarlar yükleniyor...', { configId });
        const response = await getProductSettings(companyId, configId); // ✅ companyId eklendi
        console.log('📥 [PRODUCT DISPLAY] SQL den gelen ayarlar:', response);
        
        if (response && response.success && response.data) {
          const data = response.data;
          setProductSettingsId(data.id);
          
          // ProductCardStyleJson'u parse et
          if (data.productCardStyleJson) {
            try {
              const cardStyle = JSON.parse(data.productCardStyleJson);
              console.log('📦 [PRODUCT DISPLAY] ProductCardStyle parse edildi:', cardStyle);
              
              if (cardStyle.size) {
                setCardSize(cardStyle.size);
              }
              if (cardStyle.imageHeight) {
                setImageHeight(cardStyle.imageHeight);
              }
              if (cardStyle.cardSettings) {
                setCardSettings(prev => ({ ...prev, ...cardStyle.cardSettings }));
              }
              if (cardStyle.mobileCardLayout) {
                setMobileCardLayout(cardStyle.mobileCardLayout);
              }
              if (cardStyle.elementStyles) {
                setElementStyles(prev => ({
                  productName: { ...prev.productName, ...cardStyle.elementStyles.productName },
                  sku: { ...prev.sku, ...cardStyle.elementStyles.sku },
                  price: { ...prev.price, ...cardStyle.elementStyles.price },
                  stockStatus: { ...prev.stockStatus, ...cardStyle.elementStyles.stockStatus },
                  addToCartButton: { ...prev.addToCartButton, ...cardStyle.elementStyles.addToCartButton },
                }));
              }
              if (cardStyle.imageNavigationStyle) {
                setImageNavigationStyle(cardStyle.imageNavigationStyle);
              }
              if (cardStyle.filterSidebarSettings) {
                setFilterSidebarSettings(prev => ({ ...prev, ...cardStyle.filterSidebarSettings }));
              }
              if (cardStyle.filterSidebarAdvanced) {
                const incoming = cardStyle.filterSidebarAdvanced;
                setFilterSidebarAdvanced({
                  layoutPreset: incoming.layoutPreset === 'advanced' ? 'advanced' : 'standard',
                  showSortSection: incoming.showSortSection !== false,
                  showClearAllButton: incoming.showClearAllButton !== false,
                  sections: {
                    ...defaultAdvancedFilterSettings.sections,
                    ...(incoming.sections || {}),
                  },
                });
              }
              if (cardStyle.cardRendererType === 'fixedCardV2') {
                setCardRendererType('fixedCardV2');
              } else if (cardStyle.cardRendererType === 'b2bOptimized') {
                setCardRendererType('b2bOptimized');
              } else {
                setCardRendererType('legacy');
              }
              if (cardStyle.fixedCardV2Settings) {
                const merged = { ...defaultFixedCardV2Settings, ...cardStyle.fixedCardV2Settings };
                setFixedCardV2Settings(merged);
                setFixedCardV2Inputs({
                  cardWidth: String(merged.cardWidth),
                  cardHeight: String(merged.cardHeight),
                  imageHeight: String(merged.imageHeight),
                });
              }
            } catch (e) {
              console.warn('⚠️ [PRODUCT DISPLAY] ProductCardStyleJson parse hatası:', e);
            }
          }

          const filterEnabled = cardStyleSafeBoolean(data, 'filterSidebarSettings', 'enabled');
          const layoutPreset = data?.productCardStyleJson
            ? safeGetLayoutPreset(data.productCardStyleJson)
            : 'standard';
          const mode: FilterMode = filterEnabled ? (layoutPreset === 'advanced' ? 'advanced' : 'standard') : 'off';
          setFilterMode(mode);
          
          console.log('✅ [PRODUCT DISPLAY] Ayarlar başarıyla yüklendi');
        }
      } catch (error) {
        console.error('❌ [PRODUCT DISPLAY] Ayarlar yüklenirken hata:', error);
      }
      
      // İlk yükleme bitti, artık değişiklikleri kaydet
      setTimeout(() => {
        isInitialLoadRef.current = false;
      }, 500);
    };
    
    loadProductSettings();
  }, [configId]);

  // cardSettings değiştiğinde otomatik kaydet
  useEffect(() => {
    if (isInitialLoadRef.current) {
      console.log('⏭️ [CARD SETTINGS AUTO-SAVE] İlk yükleme, kayıt atlanıyor');
      return;
    }
    
    console.log('🔄 [CARD SETTINGS AUTO-SAVE] cardSettings değişti, kayıt tetikleniyor...');
    
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
    
    autoSaveTimerRef.current = setTimeout(() => {
      autoSaveProductSettings();
    }, 1000);
  }, [cardSettings]);

  // mobileCardLayout değiştiğinde otomatik kaydet
  useEffect(() => {
    if (isInitialLoadRef.current) {
      console.log('⏭️ [MOBILE LAYOUT AUTO-SAVE] İlk yükleme, kayıt atlanıyor');
      return;
    }
    
    console.log('🔄 [MOBILE LAYOUT AUTO-SAVE] mobileCardLayout değişti, kayıt tetikleniyor...');
    
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
    
    autoSaveTimerRef.current = setTimeout(() => {
      autoSaveProductSettings();
    }, 1000);
  }, [mobileCardLayout]);

  // Sol filtre ayarları değiştiğinde otomatik kaydet
  useEffect(() => {
    if (isInitialLoadRef.current) {
      return;
    }

    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    autoSaveTimerRef.current = setTimeout(() => {
      autoSaveProductSettings();
    }, 1000);
  }, [filterSidebarSettings]);

  useEffect(() => {
    if (isInitialLoadRef.current) {
      return;
    }

    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    autoSaveTimerRef.current = setTimeout(() => {
      autoSaveProductSettings();
    }, 1000);
  }, [filterMode, filterSidebarAdvanced]);

  useEffect(() => {
    if (isInitialLoadRef.current) {
      return;
    }

    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    autoSaveTimerRef.current = setTimeout(() => {
      autoSaveProductSettings();
    }, 1000);
  }, [cardRendererType, fixedCardV2Settings]);

  useEffect(() => {
    setFixedCardV2Inputs({
      cardWidth: String(fixedCardV2Settings.cardWidth),
      cardHeight: String(fixedCardV2Settings.cardHeight),
      imageHeight: String(fixedCardV2Settings.imageHeight),
    });
  }, [fixedCardV2Settings.cardWidth, fixedCardV2Settings.cardHeight, fixedCardV2Settings.imageHeight]);

  useEffect(() => {
    if (isInitialLoadRef.current) {
      return;
    }

    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    autoSaveTimerRef.current = setTimeout(() => {
      autoSaveProductSettings();
    }, 1000);
  }, [cardSize, imageHeight]);

  useEffect(() => {
    setFilterSidebarSettings((prev) => ({
      ...prev,
      enabled: filterMode !== 'off',
      showColumnSwitcher: filterMode === 'advanced' ? prev.showColumnSwitcher : false,
    }));
  }, [filterMode]);

  const buildProductCardStyleJson = (overrides?: Partial<{ elementStyles: typeof elementStyles }>) => {
    const normalizedAdvanced: FilterSidebarAdvancedState = {
      ...filterSidebarAdvanced,
      layoutPreset: filterMode === 'advanced' ? 'advanced' : 'standard',
    };

    return JSON.stringify({
      size: cardSize,
      imageHeight: imageHeight,
      cardSettings: cardSettings,
      mobileCardLayout: mobileCardLayout,
      elementStyles: overrides?.elementStyles || elementStyles,
      imageNavigationStyle: imageNavigationStyle,
      filterSidebarSettings: {
        ...filterSidebarSettings,
        enabled: filterMode !== 'off',
        showColumnSwitcher: filterMode === 'advanced' ? filterSidebarSettings.showColumnSwitcher : false,
      },
      filterSidebarAdvanced: normalizedAdvanced,
      cardRendererType,
      fixedCardV2Settings,
    });
  };

  // Oto-kayıt fonksiyonu
  const autoSaveProductSettings = async () => {
    if (!companyId || !configId) {
      console.log('⚠️ [PRODUCT AUTO-SAVE] CompanyId veya ConfigId yok, kayıt atlanıyor');
      return;
    }
    
    try {
      console.log('💾 [PRODUCT AUTO-SAVE] Kaydediliyor...');
      
      // ProductCardStyle JSON'u oluştur
      const productCardStyleJson = buildProductCardStyleJson();
      
      const data = {
        id: productSettingsId,
        companyId: companyId,
        configId: configId,
        hideOutOfStock: displaySettings.hideOutOfStock,
        hideProductsWithoutImage: displaySettings.hideProductsWithoutImage,
        showBreadcrumb: displaySettings.showBreadcrumb,
        productCardStyleJson: productCardStyleJson,
      };
      
      console.log('📦 [PRODUCT AUTO-SAVE] Kaydedilecek veri:', data);
      
      const response = await saveProductSettings(data);
      
      if (response.success) {
        console.log('✅ [PRODUCT AUTO-SAVE] Başarılı');
        if (!productSettingsId && response.data?.id) {
          setProductSettingsId(response.data.id);
        }
      }
    } catch (error) {
      console.error('❌ [PRODUCT AUTO-SAVE] Hata:', error);
    }
  };

  const handleToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    setDisplaySettings(prev => ({ ...prev, [name]: checked }));
    onSettingsChange(name, checked);
    
    // Oto-kayıt tetikle (1 saniye debounce)
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
    
    autoSaveTimerRef.current = setTimeout(() => {
      autoSaveProductSettings();
    }, 1000);
  };
  
  const handleCardSettingToggle = (setting: keyof typeof cardSettings) => {
    setCardSettings(prev => ({
      ...prev,
      [setting]: !prev[setting]
    }));
    // Oto-kayıt artık useEffect'te yapılıyor
  };
  
  // handleSaveCardSettings kaldırıldı - artık oto-kayıt var

  const handleSnackbarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Sağ tık menüsünü açan fonksiyon
  const handleContextMenu = (event: React.MouseEvent, target: keyof typeof elementStyles) => {
    event.preventDefault();
    setContextMenu(contextMenu === null ? { mouseX: event.clientX - 2, mouseY: event.clientY - 4, target } : null);
  };

  // Menüyü kapat
  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  // Menüden "Özelleştir" tıklandığında modalı aç
  const handleEditClick = () => {
    if (contextMenu) {
      setEditingElementKey(contextMenu.target);
      setIsModalOpen(true);
      handleCloseContextMenu();
    }
  };

  // Modal'da stiller kaydedildiğinde
  const handleStyleSave = async (newStyles: any) => {
    console.log('🎯 [MODAL SAVE] handleStyleSave ÇAĞRILDI!');
    console.log('🎯 [MODAL SAVE] editingElementKey:', editingElementKey);
    console.log('🎯 [MODAL SAVE] newStyles:', newStyles);
    console.log('🎯 [MODAL SAVE] companyId:', companyId);
    console.log('🎯 [MODAL SAVE] configId:', configId);
    
    if (editingElementKey) {
      const newElementStyles = {
        ...elementStyles,
        [editingElementKey]: newStyles,
      };
      
      console.log('🎯 [MODAL SAVE] newElementStyles:', newElementStyles);
      
      setElementStyles(newElementStyles);
      setIsModalOpen(false);
      
      // Yeni değerlerle hemen kaydet (debounce olmadan)
      if (!companyId || !configId) {
        console.log('⚠️ [PRODUCT STYLE SAVE] CompanyId veya ConfigId yok, kayıt atlanıyor');
        console.log('⚠️ [PRODUCT STYLE SAVE] companyId:', companyId, 'configId:', configId);
        return;
      }
      
      try {
        console.log('💾 [PRODUCT STYLE SAVE] Modal değişiklikleri kaydediliyor...');
        
        // ProductCardStyle JSON'u oluştur - YENİ değerlerle
        const productCardStyleJson = buildProductCardStyleJson({ elementStyles: newElementStyles });
        
        console.log('📦 [PRODUCT STYLE SAVE] productCardStyleJson:', productCardStyleJson);
        
        const data = {
          id: productSettingsId,
          companyId: companyId,
          configId: configId,
          hideOutOfStock: displaySettings.hideOutOfStock,
          hideProductsWithoutImage: displaySettings.hideProductsWithoutImage,
          showBreadcrumb: displaySettings.showBreadcrumb,
          productCardStyleJson: productCardStyleJson,
        };
        
        console.log('📦 [PRODUCT STYLE SAVE] Kaydedilecek veri:', data);
        
        const response = await saveProductSettings(data);
        
        console.log('📥 [PRODUCT STYLE SAVE] Response:', response);
        
        if (response.success) {
          console.log('✅ [PRODUCT STYLE SAVE] Başarılı');
          if (!productSettingsId && response.data?.id) {
            setProductSettingsId(response.data.id);
          }
        }
      } catch (error) {
        console.error('❌ [PRODUCT STYLE SAVE] Hata:', error);
      }
    } else {
      console.log('⚠️ [MODAL SAVE] editingElementKey YOK!');
    }
  };

  // Resim geçiş butonları için sağ tık menüsü fonksiyonları
  // BU KISIM ARTIK InteractiveProductCardPreview İÇİNDE YÖNETİLİYOR
  // const handleImageNavContextMenu = ...
  // const handleCloseImageNavContextMenu = ...
  // const handleImageNavEditClick = ...

  // Resim geçiş stilini değiştirme ve kaydetme
  const handleImageNavStyleSave = async (newStyle: ImageNavigationStyle) => {
    setImageNavigationStyle(newStyle);
    
    // Oto-kayıt tetikle
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
    
    autoSaveTimerRef.current = setTimeout(() => {
      autoSaveProductSettings();
    }, 1000);
  };

  // Boyut değişiklikleri için kayıt tetikleyici
  const handleSizeChange = () => {
    console.log('📏 [PARENT] handleSizeChange ÇAĞRILDI!');
    
    // Oto-kayıt tetikle
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
    
    autoSaveTimerRef.current = setTimeout(() => {
      autoSaveProductSettings();
    }, 1000);
  };

  // InteractiveProductCardPreview'dan gelen stil değişikliklerini kaydet
  const handlePreviewStyleSave = async (elementKey: string, newStyles: any) => {
    console.log('🟡 [PARENT] handlePreviewStyleSave ÇAĞRILDI!');
    console.log('🟡 [PARENT] elementKey:', elementKey);
    console.log('🟡 [PARENT] newStyles:', newStyles);
    console.log('🟡 [PARENT] companyId:', companyId);
    console.log('🟡 [PARENT] configId:', configId);
    
    const newElementStyles = {
      ...elementStyles,
      [elementKey]: newStyles,
    };
    
    console.log('🟡 [PARENT] newElementStyles:', newElementStyles);
    
    // Yeni değerlerle hemen kaydet (debounce olmadan)
    if (!companyId || !configId) {
      console.log('⚠️ [PARENT STYLE SAVE] CompanyId veya ConfigId yok, kayıt atlanıyor');
      console.log('⚠️ [PARENT STYLE SAVE] companyId:', companyId, 'configId:', configId);
      return;
    }
    
    try {
      console.log('💾 [PARENT STYLE SAVE] Preview değişiklikleri kaydediliyor...');
      
      // ProductCardStyle JSON'u oluştur - YENİ değerlerle
      const productCardStyleJson = buildProductCardStyleJson({ elementStyles: newElementStyles });
      
      console.log('📦 [PARENT STYLE SAVE] productCardStyleJson:', productCardStyleJson);
      
      const data = {
        id: productSettingsId,
        companyId: companyId,
        configId: configId,
        hideOutOfStock: displaySettings.hideOutOfStock,
        hideProductsWithoutImage: displaySettings.hideProductsWithoutImage,
        showBreadcrumb: displaySettings.showBreadcrumb,
        productCardStyleJson: productCardStyleJson,
      };
      
      console.log('📦 [PARENT STYLE SAVE] Kaydedilecek veri:', data);
      
      const response = await saveProductSettings(data);
      
      console.log('📥 [PARENT STYLE SAVE] Response:', response);
      
      if (response.success) {
        console.log('✅ [PARENT STYLE SAVE] Başarılı');
        if (!productSettingsId && response.data?.id) {
          setProductSettingsId(response.data.id);
        }
      }
    } catch (error) {
      console.error('❌ [PARENT STYLE SAVE] Hata:', error);
    }
  };

  // Resim geçiş butonlarını render eden fonksiyon
  // BU FONKSİYON ARTIK InteractiveProductCardPreview İÇİNDE
  // const renderImageNavButtons = () => { ... };

  const allImages = mockProduct.imageUrls || [mockProduct.imageUrl];
  const currentImage = allImages[activeImageIndex];


  return (
    <Box>
      {/* SQL Integration Active Alert */}
      <Alert severity="info" sx={{ mb: 2 }}>
        ✅ <strong>{t('ecommerceProductDisplay.sqlIntegrationActive')}</strong> - {t('ecommerceProductDisplay.changesAutoSaved')}
      </Alert>

      <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom>
          Ürün Kart Motoru
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Kategori sayfasında ürünlerin nasıl listeleneceğini seçin. Her şablonun önizlemesine tıklayarak aktif edebilirsiniz.
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
            gap: 2,
            mb: 2,
          }}
        >
          <CardRendererOptionPreview
            type="legacy"
            title="Legacy (Mevcut)"
            subtitle="Mevcut firmalar için klasik ürün kartı grid düzeni. Kart boyutu ve stil ayarları özelleştirilebilir."
            selected={cardRendererType === 'legacy'}
            onSelect={() => setCardRendererType('legacy')}
          />
          <CardRendererOptionPreview
            type="fixedCardV2"
            title="Sabit Kart V2 (Yeni)"
            subtitle="Sabit boyutlu modern kartlar. Kolon sayısı seçici ve fiyat çerçevesi desteği vardır."
            selected={cardRendererType === 'fixedCardV2'}
            onSelect={() => setCardRendererType('fixedCardV2')}
          />
          <CardRendererOptionPreview
            type="b2bOptimized"
            title="B2B Optimize (Profesyonel Liste)"
            subtitle="Sol filtre paneli, tablo/liste/kart görünümü, toplu sepete ekleme ve Excel aktarımı."
            selected={cardRendererType === 'b2bOptimized'}
            onSelect={() => {
              setCardRendererType('b2bOptimized');
              setFilterMode('off');
              setFilterSidebarSettings((prev) => ({ ...prev, enabled: false }));
            }}
          />
        </Box>

        {cardRendererType === 'b2bOptimized' && (
          <Box
            sx={{
              p: 2.5,
              mb: 2,
              borderRadius: 2,
              border: '2px solid',
              borderColor: 'primary.main',
              bgcolor: 'primary.50',
            }}
          >
            <Typography variant="subtitle1" fontWeight={700} color="primary.main" gutterBottom>
              B2B Optimize Şablonu Aktif
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Kategori sayfası tam ekran optimize listeye dönüşür: sol panelde özel filtreler, kategoriler ve markalar;
              sağda arama, fiyat aralığı, tablo/liste/kart görünümü, toplu sepete ekleme, kıyaslama ve Excel aktarımı yer alır.
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 1.5 }}>
              {[
                { label: 'Sol Filtre Paneli', desc: 'Özel filtre, kategori, marka' },
                { label: 'Detaylı Tablo', desc: 'Stok, fiyat, adet kolonları' },
                { label: 'Görselli Liste', desc: 'Küçük resim + satır' },
                { label: 'Kart Grid', desc: '3 sütun ürün kartları' },
              ].map((item) => (
                <Box
                  key={item.label}
                  sx={{
                    p: 1.5,
                    borderRadius: 1.5,
                    bgcolor: '#fff',
                    border: '1px solid',
                    borderColor: 'divider',
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="caption" fontWeight={700} display="block">
                    {item.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {item.desc}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}
        {cardRendererType === 'fixedCardV2' && (
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1 }}>
            <TextField
              label="Kart Genişliği"
              type="number"
              size="small"
              value={fixedCardV2Inputs.cardWidth}
              onChange={(e) => {
                const raw = e.target.value;
                if (/^\d*$/.test(raw)) {
                  setFixedCardV2Inputs((prev) => ({ ...prev, cardWidth: raw }));
                }
              }}
              onBlur={() => {
                const parsed = Number(fixedCardV2Inputs.cardWidth);
                const safe = Number.isFinite(parsed) ? clampNumber(parsed, 180, 480) : defaultFixedCardV2Settings.cardWidth;
                setFixedCardV2Settings((prev) => ({ ...prev, cardWidth: safe }));
                setFixedCardV2Inputs((prev) => ({ ...prev, cardWidth: String(safe) }));
              }}
              inputProps={{ min: 180, max: 480, step: 1 }}
              sx={{ maxWidth: 180 }}
            />
            <TextField
              label="Kart Yüksekliği"
              type="number"
              size="small"
              value={fixedCardV2Inputs.cardHeight}
              onChange={(e) => {
                const raw = e.target.value;
                if (/^\d*$/.test(raw)) {
                  setFixedCardV2Inputs((prev) => ({ ...prev, cardHeight: raw }));
                }
              }}
              onBlur={() => {
                const parsed = Number(fixedCardV2Inputs.cardHeight);
                const safe = Number.isFinite(parsed) ? clampNumber(parsed, 260, 760) : defaultFixedCardV2Settings.cardHeight;
                setFixedCardV2Settings((prev) => ({ ...prev, cardHeight: safe }));
                setFixedCardV2Inputs((prev) => ({ ...prev, cardHeight: String(safe) }));
              }}
              inputProps={{ min: 260, max: 760, step: 1 }}
              sx={{ maxWidth: 180 }}
            />
            <TextField
              label="Resim Yüksekliği"
              type="number"
              size="small"
              value={fixedCardV2Inputs.imageHeight}
              onChange={(e) => {
                const raw = e.target.value;
                if (/^\d*$/.test(raw)) {
                  setFixedCardV2Inputs((prev) => ({ ...prev, imageHeight: raw }));
                }
              }}
              onBlur={() => {
                const parsed = Number(fixedCardV2Inputs.imageHeight);
                const safe = Number.isFinite(parsed) ? clampNumber(parsed, 120, 500) : defaultFixedCardV2Settings.imageHeight;
                setFixedCardV2Settings((prev) => ({ ...prev, imageHeight: safe }));
                setFixedCardV2Inputs((prev) => ({ ...prev, imageHeight: String(safe) }));
              }}
              inputProps={{ min: 120, max: 500, step: 1 }}
              sx={{ maxWidth: 180 }}
            />
            <TextField
              label="Sabit Kart V2 Fiyat Rengi"
              type="color"
              size="small"
              value={fixedCardV2Settings.priceColor || '#1976d2'}
              onChange={(e) => {
                const color = e.target.value || '#1976d2';
                setFixedCardV2Settings((prev) => ({ ...prev, priceColor: color }));
              }}
              sx={{ width: 180 }}
              inputProps={{ style: { height: 38, padding: 4 } }}
            />
            <TextField
              label="Fiyat Çerçeve Rengi"
              type="color"
              size="small"
              value={fixedCardV2Settings.priceFrameBorderColor || '#f5c04f'}
              onChange={(e) => {
                const color = e.target.value || '#f5c04f';
                setFixedCardV2Settings((prev) => ({ ...prev, priceFrameBorderColor: color }));
              }}
              sx={{ width: 180 }}
              inputProps={{ style: { height: 38, padding: 4 } }}
            />
            <TextField
              label="Çerçeve Yazı Rengi"
              type="color"
              size="small"
              value={fixedCardV2Settings.priceFrameTextColor || '#1f2937'}
              onChange={(e) => {
                const color = e.target.value || '#1f2937';
                setFixedCardV2Settings((prev) => ({ ...prev, priceFrameTextColor: color }));
              }}
              sx={{ width: 180 }}
              inputProps={{ style: { height: 38, padding: 4 } }}
            />
          </Box>
        )}
      </Paper>

      <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t('ecommerceProductDisplay.filteringAndView')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('ecommerceProductDisplay.filteringAndViewDescription')}
        </Typography>
        <FormGroup>
          <FormControlLabel
            control={
              <Switch
                checked={displaySettings.hideOutOfStock}
                onChange={handleToggle}
                name="hideOutOfStock"
              />
            }
            label={t('ecommerceProductDisplay.hideOutOfStock')}
          />
          <Typography variant="caption" color="text.secondary" sx={{ pl: 4, mb: 2 }}>
            {t('ecommerceProductDisplay.hideOutOfStockDescription')}
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={displaySettings.hideProductsWithoutImage}
                onChange={handleToggle}
                name="hideProductsWithoutImage"
              />
            }
            label={t('ecommerceProductDisplay.hideProductsWithoutImage')}
          />
          <Typography variant="caption" color="text.secondary" sx={{ pl: 4, mb: 2 }}>
            {t('ecommerceProductDisplay.hideProductsWithoutImageDescription')}
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={displaySettings.showBreadcrumb}
                onChange={handleToggle}
                name="showBreadcrumb"
              />
            }
            label={t('ecommerceProductDisplay.showBreadcrumbNavigation')}
          />
          <Typography variant="caption" color="text.secondary" sx={{ pl: 4 }}>
            {t('ecommerceProductDisplay.showBreadcrumbNavigationDescription')}
          </Typography>
          {displaySettings.showBreadcrumb && <BreadcrumbPreview />}
        </FormGroup>
      </Paper>

      <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2, mt: 3, opacity: cardRendererType === 'b2bOptimized' ? 0.55 : 1, pointerEvents: cardRendererType === 'b2bOptimized' ? 'none' : 'auto' }}>
        <Typography variant="h6" gutterBottom>
          {t('ecommerceProductDisplay.categoryLeftFilterSettings')}
        </Typography>
        {cardRendererType === 'b2bOptimized' && (
          <Alert severity="info" sx={{ mb: 2 }}>
            B2B Optimize şablonu kendi sol filtre panelini kullanır (Özel Filtreler, Kategoriler, Markalar). Standart sol filtre ayarları bu modda geçerli değildir.
          </Alert>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('ecommerceProductDisplay.categoryLeftFilterSettingsDescription')} Filtre paneli ve ürün listesi masaüstünde üstten hizalanır; sıralama ve kolon seçici breadcrumb satırında gösterilir.
        </Typography>
        <FormControl component="fieldset" sx={{ mb: 2 }}>
          <FormLabel component="legend">Filtre Modu</FormLabel>
          <RadioGroup
            row
            value={filterMode}
            onChange={(e) => setFilterMode(e.target.value as FilterMode)}
          >
            <FormControlLabel value="off" control={<Radio />} label="Kapalı" />
            <FormControlLabel value="standard" control={<Radio />} label="Standart" />
            <FormControlLabel value="advanced" control={<Radio />} label="Gelişmiş" />
          </RadioGroup>
        </FormControl>
        <FormGroup>
          <FormControlLabel
            control={
              <Switch
                checked={filterSidebarSettings.enabled}
                onChange={(e) => setFilterSidebarSettings(prev => ({ ...prev, enabled: e.target.checked }))}
              />
            }
            label={t('ecommerceProductDisplay.leftFilters.advancedPanelActive')}
          />
          <FormControlLabel
            control={
              <Switch
                checked={filterSidebarSettings.showResultsCount}
                onChange={(e) => setFilterSidebarSettings(prev => ({ ...prev, showResultsCount: e.target.checked }))}
              />
            }
            label="Kategori üstünde ürün sayısını göster"
          />
          <FormControlLabel
            control={
              <Switch
                checked={filterSidebarSettings.alignLeft}
                onChange={(e) => setFilterSidebarSettings(prev => ({ ...prev, alignLeft: e.target.checked }))}
                disabled={!filterSidebarSettings.enabled}
              />
            }
            label={t('ecommerceProductDisplay.leftFilters.alignLeft')}
          />
          <FormControlLabel
            control={
              <Switch
                checked={filterSidebarSettings.showClearButton}
                onChange={(e) => setFilterSidebarSettings(prev => ({ ...prev, showClearButton: e.target.checked }))}
                disabled={!filterSidebarSettings.enabled}
              />
            }
            label={t('ecommerceProductDisplay.leftFilters.clearFiltersButton')}
          />
          <FormControlLabel
            control={
              <Switch
                checked={filterSidebarSettings.showSubcategoryPanel}
                onChange={(e) => setFilterSidebarSettings(prev => ({ ...prev, showSubcategoryPanel: e.target.checked }))}
                disabled={!filterSidebarSettings.enabled}
              />
            }
            label={t('ecommerceProductDisplay.leftFilters.subcategoriesPanel')}
          />
          <FormControlLabel
            control={
              <Switch
                checked={filterSidebarSettings.showFilterPanel}
                onChange={(e) => setFilterSidebarSettings(prev => ({ ...prev, showFilterPanel: e.target.checked }))}
                disabled={!filterSidebarSettings.enabled}
              />
            }
            label={t('ecommerceProductDisplay.leftFilters.filterPanel')}
          />
          {filterMode === 'advanced' && (
            <>
              <FormControlLabel
                control={
                  <Switch
                    checked={filterSidebarSettings.showColumnSwitcher}
                    onChange={(e) => setFilterSidebarSettings(prev => ({ ...prev, showColumnSwitcher: e.target.checked }))}
                    disabled={!filterSidebarSettings.enabled || cardRendererType !== 'fixedCardV2'}
                  />
                }
                label={t('ecommerceProductDisplay.leftFilters.columnSelector')}
              />
              {cardRendererType !== 'fixedCardV2' && (
                <Typography variant="caption" color="text.secondary" sx={{ pl: 4 }}>
                  Kolon (kaçlı göster) ayarı sadece Sabit Kart V2 kart tipinde aktif olur.
                </Typography>
              )}
              <TextField
                label={t('ecommerceProductDisplay.leftFilters.defaultDesktopColumns')}
                type="number"
                size="small"
                value={filterSidebarSettings.defaultDesktopColumns}
                onChange={(e) => {
                  const parsed = Number(e.target.value);
                  const safe = Number.isFinite(parsed) ? Math.max(2, Math.min(5, parsed)) : 4;
                  setFilterSidebarSettings(prev => ({ ...prev, defaultDesktopColumns: safe }));
                }}
                disabled={!filterSidebarSettings.enabled || !filterSidebarSettings.showColumnSwitcher}
                sx={{ mt: 1, maxWidth: 220 }}
                inputProps={{ min: 2, max: 5, step: 1 }}
              />
            </>
          )}
          <FormControlLabel
            control={
              <Switch
                checked={filterSidebarSettings.showCategoryFilter}
                onChange={(e) => setFilterSidebarSettings(prev => ({ ...prev, showCategoryFilter: e.target.checked }))}
                disabled={!filterSidebarSettings.enabled || !filterSidebarSettings.showFilterPanel}
              />
            }
            label={t('ecommerceProductDisplay.leftFilters.categoryFilter')}
          />
          <FormControlLabel
            control={
              <Switch
                checked={filterSidebarSettings.showBrandFilter}
                onChange={(e) => setFilterSidebarSettings(prev => ({ ...prev, showBrandFilter: e.target.checked }))}
                disabled={!filterSidebarSettings.enabled || !filterSidebarSettings.showFilterPanel}
              />
            }
            label={t('ecommerceProductDisplay.leftFilters.brandFilter')}
          />
          <FormControlLabel
            control={
              <Switch
                checked={filterSidebarSettings.showPriceFilter}
                onChange={(e) => setFilterSidebarSettings(prev => ({ ...prev, showPriceFilter: e.target.checked }))}
                disabled={!filterSidebarSettings.enabled || !filterSidebarSettings.showFilterPanel}
              />
            }
            label={t('ecommerceProductDisplay.leftFilters.priceRangeFilter')}
          />
          <FormControlLabel
            control={
              <Switch
                checked={filterSidebarSettings.showVariantFilters}
                onChange={(e) => setFilterSidebarSettings(prev => ({ ...prev, showVariantFilters: e.target.checked }))}
                disabled={!filterSidebarSettings.enabled || !filterSidebarSettings.showFilterPanel}
              />
            }
            label={t('ecommerceProductDisplay.leftFilters.variantFilters')}
          />
          <FormControlLabel
            control={
              <Switch
                checked={filterSidebarSettings.showOptionCounts}
                onChange={(e) => setFilterSidebarSettings(prev => ({ ...prev, showOptionCounts: e.target.checked }))}
                disabled={!filterSidebarSettings.enabled || !filterSidebarSettings.showFilterPanel}
              />
            }
            label={t('ecommerceProductDisplay.leftFilters.showOptionCounts')}
          />
          <FormControlLabel
            control={
              <Switch
                checked={filterSidebarSettings.showInStockToggle}
                onChange={(e) => setFilterSidebarSettings(prev => ({ ...prev, showInStockToggle: e.target.checked }))}
                disabled={!filterSidebarSettings.enabled}
              />
            }
            label="Stoktaki Ürünler seçeneğini göster"
          />
        </FormGroup>
        {filterMode !== 'off' && filterSidebarSettings.enabled && (
          <FilterPanelPreview
            mode={filterMode === 'advanced' ? 'advanced' : 'standard'}
            showColumnSwitcher={filterSidebarSettings.showColumnSwitcher && cardRendererType === 'fixedCardV2'}
          />
        )}
        {filterMode === 'advanced' && (
          <Box sx={{ mt: 2, p: 2, border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Gelişmiş Filtre Bölümleri
            </Typography>
            <FormGroup>
              <FormControlLabel
                control={
                  <Switch
                    checked={filterSidebarAdvanced.showSortSection}
                    onChange={(e) =>
                      setFilterSidebarAdvanced((prev) => ({ ...prev, showSortSection: e.target.checked }))
                    }
                  />
                }
                label="Sırala bölümü"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={filterSidebarAdvanced.showClearAllButton}
                    onChange={(e) =>
                      setFilterSidebarAdvanced((prev) => ({ ...prev, showClearAllButton: e.target.checked }))
                    }
                  />
                }
                label="Filtreleri temizle butonu"
              />
              {(Object.keys(filterSidebarAdvanced.sections) as FilterSectionKey[])
                .filter((sectionKey) => sectionKey !== 'sort')
                .map((sectionKey) => (
                <FormControlLabel
                  key={sectionKey}
                  control={
                    <Switch
                      checked={filterSidebarAdvanced.sections[sectionKey].enabled}
                      onChange={(e) =>
                        setFilterSidebarAdvanced((prev) => ({
                          ...prev,
                          sections: {
                            ...prev.sections,
                            [sectionKey]: { enabled: e.target.checked },
                          },
                        }))
                      }
                    />
                  }
                  label={`${sectionKey} bölümü`}
                />
              ))}
            </FormGroup>
          </Box>
        )}
      </Paper>

      {cardRendererType === 'legacy' && (
        <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2, mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            {t('ecommerceProductDisplay.productCardDesign')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1, lineHeight: 1.2 }}>
            {t('ecommerceProductDisplay.productCardDesignDescription1')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1, lineHeight: 1.2 }}>
            {t('ecommerceProductDisplay.productCardDesignDescription2')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.2 }}>
            {t('ecommerceProductDisplay.productCardDesignDescription3')}
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <InteractiveProductCardPreview
                cardSize={cardSize}
                setCardSize={setCardSize}
                imageHeight={imageHeight}
                setImageHeight={setImageHeight}
                cardSettings={cardSettings}
                elementStyles={elementStyles}
                setElementStyles={setElementStyles}
                imageNavigationStyle={imageNavigationStyle}
                setImageNavigationStyle={handleImageNavStyleSave} // Kaydetme fonksiyonunu veriyoruz
                activeImageIndex={activeImageIndex}
                setActiveImageIndex={setActiveImageIndex}
                onStyleSave={handlePreviewStyleSave} // ✅ SQL'e kaydetmek için callback
                onSizeChange={handleSizeChange} // ✅ Boyut değişiklikleri için callback
              />
          </Box>
          
          {/* Kart Ayarları Bölümü */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t('ecommerceProductDisplay.cardSettings')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('ecommerceProductDisplay.cardSettingsDescription')}
            </Typography>
            
            <FormGroup>
              <FormControlLabel
                control={
                  <Switch
                    checked={cardSettings.showProductName}
                    onChange={() => handleCardSettingToggle('showProductName')}
                  />
                }
                label={t('ecommerceProductDisplay.cardFields.showProductName')}
              />
              <Typography variant="caption" color="text.secondary" sx={{ pl: 4, mb: 1 }}>
                {t('ecommerceProductDisplay.cardFields.showProductNameDescription')}
              </Typography>

              <FormControlLabel
                control={
                  <Switch
                    checked={cardSettings.showSku}
                    onChange={() => handleCardSettingToggle('showSku')}
                  />
                }
                label={t('ecommerceProductDisplay.cardFields.showSku')}
              />
              <Typography variant="caption" color="text.secondary" sx={{ pl: 4, mb: 1 }}>
                {t('ecommerceProductDisplay.cardFields.showSkuDescription')}
              </Typography>

              <FormControlLabel
                control={
                  <Switch
                    checked={cardSettings.showPrice}
                    onChange={() => handleCardSettingToggle('showPrice')}
                  />
                }
                label={t('ecommerceProductDisplay.cardFields.showPrice')}
              />
              <Typography variant="caption" color="text.secondary" sx={{ pl: 4, mb: 1 }}>
                {t('ecommerceProductDisplay.cardFields.showPriceDescription')}
              </Typography>

              <FormControlLabel
                control={
                  <Switch
                    checked={cardSettings.showStockStatus}
                    onChange={() => handleCardSettingToggle('showStockStatus')}
                  />
                }
                label={t('ecommerceProductDisplay.cardFields.showStockStatus')}
              />
              <Typography variant="caption" color="text.secondary" sx={{ pl: 4, mb: 1 }}>
                {t('ecommerceProductDisplay.cardFields.showStockStatusDescription')}
              </Typography>

              <Box sx={{ pl: 1, pr: 1, mb: 2, maxWidth: 320 }}>
                <TextField
                  type="number"
                  size="small"
                  fullWidth
                  label={t('ecommerceProductDisplay.cardFields.lowStockThreshold')}
                  value={cardSettings.lowStockThreshold ?? 0}
                  inputProps={{ min: 0, step: 1 }}
                  onChange={(e) => {
                    const raw = parseInt(e.target.value, 10);
                    const next = Number.isFinite(raw) ? Math.max(0, raw) : 0;
                    setCardSettings((prev) => ({ ...prev, lowStockThreshold: next }));
                  }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  {t('ecommerceProductDisplay.cardFields.lowStockThresholdDescription')}
                </Typography>
              </Box>

              <FormControlLabel
                control={
                  <Switch
                    checked={cardSettings.showAddToCartButton}
                    onChange={() => handleCardSettingToggle('showAddToCartButton')}
                  />
                }
                label={t('ecommerceProductDisplay.cardFields.showAddToCartButton')}
              />
              <Typography variant="caption" color="text.secondary" sx={{ pl: 4 }}>
                {t('ecommerceProductDisplay.cardFields.showAddToCartButtonDescription')}
              </Typography>
            </FormGroup>

            {/* Mobile Card Layout */}
            <FormControl component="fieldset" sx={{ mt: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <FormLabel component="legend" sx={{ fontWeight: 'medium', fontSize: '0.9rem' }}>{t('ecommerceProductDisplay.mobileCardLayout')}</FormLabel>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                {t('ecommerceProductDisplay.mobileCardLayoutDescription')}
              </Typography>
              <RadioGroup
                row
                aria-label={t('ecommerceProductDisplay.mobileCardLayoutAriaLabel')}
                name="mobile-card-layout"
                value={mobileCardLayout}
                onChange={(e) => {
                  setMobileCardLayout(e.target.value as 'single' | 'double');
                  // Oto-kayıt artık useEffect'te yapılıyor
                }}
              >
                <FormControlLabel value="double" control={<Radio />} label={t('ecommerceProductDisplay.mobileCardLayoutOptions.twoPerRow')} />
                <FormControlLabel value="single" control={<Radio />} label={t('ecommerceProductDisplay.mobileCardLayoutOptions.onePerRow')} />
              </RadioGroup>
            </FormControl>
          </Box>
        </Paper>
      )}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Sağ tık menüsü */}
      <Menu
        open={contextMenu !== null}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        <MenuItem onClick={handleEditClick}>{t('ecommerceProductDisplay.customize')}</MenuItem>
      </Menu>

      {/* Resim geçiş butonları için sağ tık menüsü InteractiveProductCardPreview'a taşındı */}
      
      {/* Resim geçiş stili düzenleme modalı InteractiveProductCardPreview'a taşındı */}
      
      {/* Stil düzenleme modalı InteractiveProductCardPreview'a taşındı, ancak state'ler burada yönetildiği için burada kalabilir veya taşınabilir. Şimdilik burada bırakıyorum. */}
      {editingElementKey && (
        <ElementStyleEditorModal
          open={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          elementKey={editingElementKey}
          currentStyles={elementStyles[editingElementKey]}
          onSave={handleStyleSave}
        />
      )}
    </Box>
  );
};

export default ProductDisplaySettings; 

const safeGetLayoutPreset = (productCardStyleJson: string): 'standard' | 'advanced' => {
  try {
    const parsed = JSON.parse(productCardStyleJson);
    return parsed?.filterSidebarAdvanced?.layoutPreset === 'advanced' ? 'advanced' : 'standard';
  } catch {
    return 'standard';
  }
};

const cardStyleSafeBoolean = (
  data: any,
  parentKey: string,
  key: string
): boolean => {
  try {
    const parsed = data?.productCardStyleJson ? JSON.parse(data.productCardStyleJson) : null;
    return Boolean(parsed?.[parentKey]?.[key]);
  } catch {
    return false;
  }
};